const RETURN_URL_STORAGE_KEY = 'ReactStorage__AuthenticationReturnUrl';
const DEFAULT_RETURN_URL = '/';

type StoredReturnUrl = {
  url: string;
  userId: number | null;
};

function decodeReturnUrl(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function sanitizeReturnUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || !value) {
    return null;
  }

  const decodedValue = value.startsWith('/') ? value : decodeReturnUrl(value);
  if (!decodedValue.startsWith('/') || decodedValue.startsWith('//')) {
    return null;
  }

  try {
    const url = new URL(decodedValue, window.location.origin);
    const normalizedPathname =
      url.pathname.replace(/\/+$/, '') || '/';
    const isAuthRoute =
      normalizedPathname === '/auth' ||
      normalizedPathname.startsWith('/auth/');
    if (
      url.origin !== window.location.origin ||
      isAuthRoute
    ) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

function getStoredReturnUrl(): StoredReturnUrl | null {
  try {
    const storedValue = window.sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(storedValue) as Partial<StoredReturnUrl>;
      const url = sanitizeReturnUrl(parsedValue.url);
      if (
        url &&
        (typeof parsedValue.userId === 'number' ||
          parsedValue.userId === null)
      ) {
        return { url, userId: parsedValue.userId };
      }
    } catch {
      const legacyUrl = sanitizeReturnUrl(storedValue);
      return legacyUrl ? { url: legacyUrl, userId: null } : null;
    }

    return null;
  } catch {
    return null;
  }
}

function getReturnUrl(value?: string, userId?: number | null): string {
  const storedReturnUrl = getStoredReturnUrl();
  if (
    userId != null &&
    storedReturnUrl?.userId != null &&
    storedReturnUrl.userId !== userId
  ) {
    return DEFAULT_RETURN_URL;
  }

  return (
    sanitizeReturnUrl(value) ??
    storedReturnUrl?.url ??
    DEFAULT_RETURN_URL
  );
}

/**
 * Stores a valid return URL without replacing an existing value when the
 * candidate is invalid. The returned URL remains usable as a query parameter
 * even when session storage is unavailable.
 */
function saveReturnUrl(value?: string, userId?: number | null): string {
  const sanitizedReturnUrl = sanitizeReturnUrl(value);
  const storedReturnUrl = getStoredReturnUrl();
  const returnUrl =
    sanitizedReturnUrl ?? storedReturnUrl?.url ?? DEFAULT_RETURN_URL;
  if (!sanitizedReturnUrl) {
    return returnUrl;
  }

  const storedUserId =
    storedReturnUrl?.url === returnUrl ? storedReturnUrl.userId : null;
  const returnUrlOwner = userId === undefined ? storedUserId : userId;

  try {
    window.sessionStorage.setItem(
      RETURN_URL_STORAGE_KEY,
      JSON.stringify({
        url: returnUrl,
        userId: returnUrlOwner ?? null,
      } satisfies StoredReturnUrl),
    );
  } catch {
    // The query parameter remains available when session storage is blocked.
  }
  return returnUrl;
}

function clearReturnUrl(): void {
  try {
    window.sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
  } catch {
    // No cleanup is necessary when session storage is blocked.
  }
}

export { clearReturnUrl, getReturnUrl, saveReturnUrl };
